import { zodResolver } from "@hookform/resolvers/zod";
import { clsx } from "clsx";
import { ArrowLeft, Loader2, Pencil, Plus } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";

import {
	APIError,
	ROOT_PARENT_ID,
	type StageKind,
	type UpdateStageInput,
} from "@/adapters/api";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { usePermissions } from "@/hooks/use-permissions.ts";
import { useCreateStage } from "../../hooks/use-create-stage.ts";
import { useSetStageValue } from "../../hooks/use-set-stage-value.ts";
import { useUpdateStage } from "../../hooks/use-update-stage.ts";
import {
	type StageFormValues,
	stageSchema,
} from "../../schemas/stage-schema.ts";
import type { Stage } from "../../types.ts";
import { defaultKind, STAGE_KIND, STAGE_KINDS } from "../../utils/kind.ts";
import { isStagesLockedError } from "../../utils/lock.ts";
import { STAGE_STATUS, STAGE_STATUSES } from "../../utils/status.ts";
import { descendantIds, hasChildren } from "../../utils/tree.ts";
import { StageCombobox } from "../stage-combobox";

const VIOLATION_FIELDS: Record<string, keyof StageFormValues> = {
	name: "name",
	description: "description",
	parent_stage_id: "parentId",
	status: "status",
	kind: "kind",
	value: "valueCents",
};

const VIOLATION_MESSAGES: Record<string, string> = {
	parent_is_service: "Um serviço não pode ter itens abaixo dele.",
	service_has_children: "Um item com sub-itens não pode ser um serviço.",
	invalid: "Selecione um tipo válido.",
	not_leaf: "Apenas itens sem sub-itens podem ter valor.",
	out_of_range: "Informe um valor de até R$ 99.999.999.999,99.",
};

const REASON_MESSAGES: Record<string, string> = {
	cycle: "Uma etapa não pode ser movida para dentro de uma de suas sub-etapas.",
};

export function StageForm({
	mode,
	constructionId,
	stages,
	stage,
	defaultParentId,
	defaultKind: requestedKind,
	listPath,
}: {
	mode: "create" | "edit";
	constructionId: string;
	stages: Stage[];
	stage?: Stage;
	defaultParentId?: string;
	defaultKind?: StageKind;
	listPath: string;
}) {
	const navigate = useNavigate();
	const { has } = usePermissions();
	const [formError, setFormError] = useState<string | null>(null);
	const createStage = useCreateStage(constructionId);
	const updateStage = useUpdateStage(constructionId);
	const setStageValue = useSetStageValue(constructionId);
	const isPending =
		createStage.isPending || updateStage.isPending || setStageValue.isPending;

	const isLeaf = !stage || !hasChildren(stages, stage.id);
	const showValue = isLeaf && has("stage_values:manage");
	const initialValue =
		(stage && stages.find((item) => item.id === stage.id)?.valueCents) ?? null;
	const initialParentId = stage?.parentId ?? defaultParentId ?? "";

	const {
		register,
		handleSubmit,
		control,
		setError,
		setValue,
		getFieldState,
		watch,
		formState: { errors },
	} = useForm<StageFormValues>({
		resolver: zodResolver(stageSchema),
		defaultValues: {
			name: stage?.name ?? "",
			description: stage?.description ?? "",
			parentId: initialParentId,
			kind:
				stage?.kind ??
				requestedKind ??
				defaultKind(initialParentId || undefined),
			valueCents: initialValue,
			status: stage?.status,
		},
	});

	const kind = watch("kind");
	const excludeIds = new Set([
		...stages.filter((item) => item.kind === "service").map((item) => item.id),
		...(mode === "edit" && stage
			? [stage.id, ...descendantIds(stages, stage.id)]
			: []),
	]);

	function applyAPIError(err: unknown, fallback: string) {
		if (isStagesLockedError(err)) {
			navigate(listPath);
			return;
		}
		if (err instanceof APIError) {
			let matchedField = false;
			for (const [field, message] of Object.entries(err.violations)) {
				const formField = VIOLATION_FIELDS[field];
				if (formField) {
					setError(formField, {
						message: VIOLATION_MESSAGES[message] ?? message,
					});
					matchedField = true;
				}
			}
			if (!matchedField) {
				setFormError(REASON_MESSAGES[err.reason] || err.message || fallback);
			}
			return;
		}
		setFormError(err instanceof Error ? err.message : fallback);
	}

	async function onSubmit(values: StageFormValues) {
		setFormError(null);

		if (mode === "create") {
			let created: { id: string };
			try {
				created = await createStage.mutateAsync({
					name: values.name,
					description: values.description?.trim() || undefined,
					parentStageId: values.parentId || undefined,
					kind: values.kind,
				});
			} catch (err) {
				applyAPIError(err, "Houve um erro ao criar a etapa.");
				return;
			}
			if (showValue && values.valueCents !== null) {
				try {
					await setStageValue.mutateAsync({
						stageId: created.id,
						valueCents: values.valueCents,
					});
				} catch (err) {
					if (!isStagesLockedError(err)) {
						toast.error("Etapa criada, mas houve um erro ao salvar o valor.");
					}
					navigate(listPath);
					return;
				}
			}
			toast.success("Etapa criada com sucesso!");
			navigate(listPath);
			return;
		}

		if (!stage) return;
		const input: UpdateStageInput = {};
		if (values.name !== stage.name) input.name = values.name;
		if (values.kind !== stage.kind) input.kind = values.kind;
		if (values.status && values.status !== stage.status) {
			input.status = values.status;
		}
		const description = values.description?.trim() ?? "";
		if (description && description !== (stage.description ?? "")) {
			input.description = description;
		}
		const parentId = values.parentId ?? "";
		if (parentId !== (stage.parentId ?? "")) {
			input.parentStageId = parentId || ROOT_PARENT_ID;
		}

		const valueChanged = showValue && values.valueCents !== initialValue;

		if (Object.keys(input).length === 0 && !valueChanged) {
			toast.info("Nenhuma alteração para salvar.");
			navigate(listPath);
			return;
		}

		try {
			if (Object.keys(input).length > 0) {
				await updateStage.mutateAsync({ stageId: stage.id, input });
			}
			if (valueChanged) {
				await setStageValue.mutateAsync({
					stageId: stage.id,
					valueCents: values.valueCents,
				});
			}
			toast.success("Etapa atualizada com sucesso!");
			navigate(listPath);
		} catch (err) {
			applyAPIError(err, "Houve um erro ao atualizar a etapa.");
		}
	}

	function handleParentChange(next: string) {
		if (mode === "create" && !getFieldState("kind").isDirty) {
			setValue("kind", defaultKind(next || undefined));
		}
	}

	const SubmitIcon = mode === "create" ? Plus : Pencil;
	const serviceBlocked = mode === "edit" && !isLeaf;

	return (
		<form onSubmit={handleSubmit(onSubmit)} noValidate>
			<Card>
				<CardHeader>
					<CardTitle>Informações</CardTitle>
					<CardDescription>
						Etapas organizam o andamento da obra em uma árvore de trabalhos
						e sub-trabalhos.
					</CardDescription>
				</CardHeader>
				<CardContent className="stage-form__content">
					<div className="form-group">
						<Label htmlFor="stage-name">Nome</Label>
						<Input
							id="stage-name"
							placeholder="Ex.: Fundação"
							aria-invalid={!!errors.name}
							{...register("name")}
						/>
						{errors.name && <p className="form-error">{errors.name.message}</p>}
					</div>

					<div className="form-group">
						<Label htmlFor="stage-description">Descrição</Label>
						<Textarea
							id="stage-description"
							placeholder="Detalhes da etapa (opcional)"
							aria-invalid={!!errors.description}
							{...register("description")}
						/>
						{mode === "edit" && stage?.description && (
							<p className="stage-form__hint">
								A descrição não pode ser removida, apenas alterada.
							</p>
						)}
						{errors.description && (
							<p className="form-error">{errors.description.message}</p>
						)}
					</div>

					<div className="form-group">
						<Label htmlFor="stage-parent">Etapa pai</Label>
						<Controller
							control={control}
							name="parentId"
							render={({ field }) => (
								<StageCombobox
									id="stage-parent"
									stages={stages}
									value={field.value ?? ""}
									onChange={(next) => {
										field.onChange(next);
										handleParentChange(next);
									}}
									excludeIds={excludeIds}
								/>
							)}
						/>
						{errors.parentId && (
							<p className="form-error">{errors.parentId.message}</p>
						)}
					</div>

					<div className="form-group">
						<Label htmlFor="stage-kind">Tipo</Label>
						<Controller
							control={control}
							name="kind"
							render={({ field }) => (
								<Select value={field.value} onValueChange={field.onChange}>
									<SelectTrigger
										id="stage-kind"
										className="stage-form__status-select"
										aria-invalid={!!errors.kind}
									>
										<SelectValue placeholder="Selecione o tipo" />
									</SelectTrigger>
									<SelectContent>
										{STAGE_KINDS.map((option) => (
											<SelectItem
												key={option}
												value={option}
												disabled={option === "service" && serviceBlocked}
											>
												{STAGE_KIND[option].label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							)}
						/>
						{serviceBlocked && (
							<p className="stage-form__hint">
								Itens com sub-itens não podem ser do tipo Serviço.
							</p>
						)}
						{errors.kind && <p className="form-error">{errors.kind.message}</p>}
					</div>

					{showValue && (
						<div className="form-group">
							<Label htmlFor="stage-value">Valor</Label>
							<Controller
								control={control}
								name="valueCents"
								render={({ field }) => (
									<CurrencyInput
										id="stage-value"
										placeholder="R$ 0,00"
										aria-invalid={!!errors.valueCents}
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
									/>
								)}
							/>
							{errors.valueCents && (
								<p className="form-error">{errors.valueCents.message}</p>
							)}
						</div>
					)}

					{mode === "edit" && (
						<div className="form-group">
							<Label htmlFor="stage-status">Status</Label>
							<Controller
								control={control}
								name="status"
								render={({ field }) => (
									<Select value={field.value} onValueChange={field.onChange}>
										<SelectTrigger
											id="stage-status"
											className="stage-form__status-select"
										>
											<SelectValue placeholder="Selecione o status" />
										</SelectTrigger>
										<SelectContent>
											{STAGE_STATUSES.map((status) => (
												<SelectItem key={status} value={status}>
													<span
														aria-hidden
														className="status-dot"
														data-status={status}
													/>
													{STAGE_STATUS[status].label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								)}
							/>
							{errors.status && (
								<p className="form-error">{errors.status.message}</p>
							)}
						</div>
					)}

					{formError && (
						<p role="alert" className="stage-form__error">
							{formError}
						</p>
					)}
				</CardContent>
				<CardFooter className="stage-form__footer">
					<Button asChild variant="ghost">
						<Link
							to={listPath}
							aria-disabled={isPending}
							className={clsx("stage-form__cancel", isPending && "is-disabled")}
						>
							<ArrowLeft />
							Cancelar
						</Link>
					</Button>
					<Button type="submit" disabled={isPending}>
						{isPending ? (
							<Loader2 className="stage-form__spinner" />
						) : (
							<SubmitIcon />
						)}
						{mode === "create"
							? `Adicionar ${STAGE_KIND[kind].noun}`
							: "Salvar alterações"}
					</Button>
				</CardFooter>
			</Card>
		</form>
	);
}

export function StageFormSkeleton({
	withStatus = false,
}: {
	withStatus?: boolean;
}) {
	return (
		<Card>
			<CardHeader>
				<Skeleton className="stage-form__skeleton-title" />
				<Skeleton className="stage-form__skeleton-desc" />
			</CardHeader>
			<CardContent className="stage-form__content">
				<div className="form-group">
					<Skeleton className="stage-form__skeleton-label" />
					<Skeleton className="stage-form__skeleton-field" />
				</div>
				<div className="form-group">
					<Skeleton className="stage-form__skeleton-label" />
					<Skeleton className="stage-form__skeleton-field stage-form__skeleton-field--area" />
				</div>
				<div className="form-group">
					<Skeleton className="stage-form__skeleton-label" />
					<Skeleton className="stage-form__skeleton-field" />
				</div>
				{withStatus && (
					<div className="form-group">
						<Skeleton className="stage-form__skeleton-label" />
						<Skeleton className="stage-form__skeleton-field" />
					</div>
				)}
			</CardContent>
			<CardFooter className="stage-form__footer">
				<Skeleton className="stage-form__skeleton-button" />
				<Skeleton className="stage-form__skeleton-button" />
			</CardFooter>
		</Card>
	);
}
